import React, { useState } from 'react';
import { FiSearch, FiFilter, FiCalendar, FiX, FiRotateCcw } from 'react-icons/fi';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { useSearch } from '../../contexts/SearchContext';
import DateRangePicker from '../DateRangePicker/DateRangePicker';
import { getPopularKeywords, getRecentSearches } from '../../services/searchService';
import './SearchFilters.css';

/// SearchFiltersコンポーネントのProps
interface SearchFiltersProps {
  isModal?: boolean;
  onClose?: () => void;
  onSearch?: () => void;
}

/// 検索フィルタコンポーネント
const SearchFilters: React.FC<SearchFiltersProps> = ({
  isModal = false,
  onClose,
  onSearch
}) => {
  const { state, dispatch } = useSearch();
  const [tempKeyword, setTempKeyword] = useState(state.keyword);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  // 人気のキーワードと検索履歴
  const popularKeywords = getPopularKeywords();
  const recentSearches = getRecentSearches();

  /// キーワード変更ハンドラー
  const handleKeywordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTempKeyword(e.target.value);
  };

  /// キーワード適用（検索実行して閉じる）
  const handleKeywordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch();
  };

  /// カテゴリ変更ハンドラー
  const handleCategoryChange = (category: 'all' | 'videos') => {
    dispatch({ type: 'SET_CATEGORY', payload: category });
  };

  /// フィルタリセット
  const handleReset = () => {
    setTempKeyword('');
    dispatch({ type: 'RESET_FILTERS' });
  };

  /// 検索実行
  const handleSearch = () => {
    dispatch({ type: 'SET_KEYWORD', payload: tempKeyword });
    if (onSearch) {
      onSearch();
    }
    if (isModal && onClose) {
      onClose();
    }
  };

  /// キーワード候補クリック
  const handleKeywordSuggestion = (keyword: string) => {
    setTempKeyword(keyword);
    dispatch({ type: 'SET_KEYWORD', payload: keyword });
  };

  /// 日付範囲の表示テキスト
  const getDateRangeText = () => {
    const { startDate, endDate } = state.dateRange;
    if (startDate && endDate) {
      return `${format(startDate, 'yyyy/MM/dd', { locale: ja })} - ${format(endDate, 'yyyy/MM/dd', { locale: ja })}`;
    } else if (startDate) {
      return `${format(startDate, 'yyyy/MM/dd', { locale: ja })} 以降`;
    }
    return '期間を選択';
  };

  const containerClass = `search-filters ${isModal ? 'search-filters--modal' : ''}`;

  return (
    <div className={containerClass}>
      {isModal && (
        <div className="search-filters__header">
          <h3 className="search-filters__title">
            {FiFilter({ className: "search-filters__icon" })}
            検索フィルタ
          </h3>
          <button 
            className="search-filters__close"
            onClick={onClose}
            aria-label="閉じる"
          >
            {FiX({})}
          </button>
        </div>
      )}

      <div className="search-filters__content">
        {/* キーワード検索 */}
        <div className="search-filters__section">
          <label className="search-filters__label">
            {FiSearch({ className: "search-filters__label-icon" })}
            キーワード
          </label>
          <form onSubmit={handleKeywordSubmit} className="search-filters__search-form">
            <input
              type="text"
              className="search-filters__search-input"
              placeholder="動画タイトルや説明を検索..."
              value={tempKeyword}
              onChange={handleKeywordChange}
            />
            <button 
              type="submit"
              className="search-filters__search-btn"
              aria-label="検索"
            >
              {FiSearch({})}
            </button>
          </form>
        </div>

        {/* キーワード候補 */}
        {(popularKeywords.length > 0 || recentSearches.length > 0) && (
          <div className="search-filters__section">
            <div className="search-filters__suggestions">
              {recentSearches.length > 0 && (
                <div className="search-filters__suggestion-group">
                  <span className="search-filters__suggestion-title">最近の検索</span>
                  <div className="search-filters__suggestion-tags">
                    {recentSearches.slice(0, 5).map((keyword, index) => (
                      <button
                        key={index}
                        className="search-filters__suggestion-tag search-filters__suggestion-tag--recent"
                        onClick={() => handleKeywordSuggestion(keyword)}
                      >
                        {keyword}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {popularKeywords.length > 0 && (
                <div className="search-filters__suggestion-group">
                  <span className="search-filters__suggestion-title">人気のキーワード</span>
                  <div className="search-filters__suggestion-tags">
                    {popularKeywords.map((keyword, index) => (
                      <button
                        key={index}
                        className="search-filters__suggestion-tag search-filters__suggestion-tag--popular"
                        onClick={() => handleKeywordSuggestion(keyword)}
                      >
                        {keyword}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* カテゴリ選択 */}
        <div className="search-filters__section">
          <label className="search-filters__label">カテゴリ</label>
          <div className="search-filters__category-group">
            {[
              { value: 'all', label: 'すべて' },
              { value: 'videos', label: '動画' }
            ].map(({ value, label }) => (
              <button
                key={value}
                className={`search-filters__category-btn ${
                  state.category === value ? 'search-filters__category-btn--active' : ''
                }`}
                onClick={() => handleCategoryChange(value as 'all' | 'videos')}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* 日付範囲選択 */}
        <div className="search-filters__section">
          <label className="search-filters__label">
            {FiCalendar({ className: "search-filters__label-icon" })}
            投稿日期間
          </label>
          <button
            className="search-filters__date-btn"
            onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
            aria-expanded={isDatePickerOpen}
          >
            {getDateRangeText()}
            {FiCalendar({ className: "search-filters__date-icon" })}
          </button>
          {isDatePickerOpen && (
            <div className="search-filters__datepicker-container">
              <DateRangePicker onClose={() => setIsDatePickerOpen(false)} />
            </div>
          )}
        </div>

        {/* アクションボタン */}
        <div className="search-filters__actions">
          <button
            className="search-filters__reset-btn"
            onClick={handleReset}
          >
            {FiRotateCcw({ className: "search-filters__btn-icon" })}
            リセット
          </button>
          
          {isModal && (
            <button
              className="search-filters__apply-btn"
              onClick={handleSearch}
            >
              {FiSearch({ className: "search-filters__btn-icon" })}
              検索実行
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchFilters; 